import {MediaPanelCardControl} from '@wandb/weave/common/components/MediaCard';
import {RepresentationType} from '@wandb/weave/common/types/libs/nglviewer';
import {MaskOptions, MediaCardType} from '@wandb/weave/common/types/media';

import {DirectUrlQueryQuery} from '../generated/graphql';
import * as PanelTypes from '../state/views/panel/types';
import {PartRefFromObjSchema} from '../state/views/types';
import {RunHistoryRow, RunSignature, RunWithHistory} from '../types/run';
import {Media} from '../util/runTypes';
import type {
  Axis,
  MediaBrowserPanelConfig,
  NonEmptyTile,
} from './PanelMediaBrowser/common';

export * from '@wandb/weave/common/components/MediaCard';

// This type adds the special _wandb field from config to the run for
// dirty state tunneling
export type RunWithHistoryAndMediaWandb = RunWithHistory & {
  _wandb: any;
  entityName: string;
  projectName: string;
  color: string | undefined;
};

export type MoleculeConfig = {
  representation?: RepresentationType;
};

export type TileMediaSpec = Pick<
  NonEmptyTile,
  'runSignature' | 'mediaKey' | 'mediaStep' | 'mediaIndex' | 'run'
> & {
  mediaType: MediaCardType;
  mediaSliderKey: string;
  mediaSliderKeyValue?: number;
  pixelated?: boolean;
};

export type Thumbnails = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<NonNullable<DirectUrlQueryQuery['project']>['run']>['files']
    >['edges']
  >[number]['node']
>['thumbnails'];

export type TileMedia = {
  mediaKey: string;
  step: number;
  index?: number;
  historyRow: RunHistoryRow;
  blob: Blob;
  objectURL: string;
  filePath: string;
  directURL: string; // represents a direct link to an asset in GCS
  internalURL?: string; // the same asset served by Gorilla
  mediaSliderKey: string;
  mediaSliderKeyValue?: number;
  thumbnails?: Thumbnails;
};

export type TileMediaTracking = {
  availableFilesReceivedPerformanceMs: number;
  directUrlReceivedPerformanceMs: number;
  blobFetchCompletePerformanceMs: number;
  dataCachedPerformanceMs: number;
};

export interface MediaCardProps {
  run: RunWithHistoryAndMediaWandb;
  runSignature: RunSignature;
  customRunName?: string;

  globalStep: number;

  mediaKey: string;
  mediaIndex: number;

  width: number;
  height: number;

  actualSize: boolean;

  labels?: Axis[];
  runNotes?: string;

  single?: boolean;

  mediaWidth?: number;
  mediaHeight?: number;

  disableRunLink?: boolean;
  // prevent adding the run label to the media card
  omitRunLabel?: boolean;

  maskOptions?: MaskOptions;

  // This is for metadata to connect the media browser and the cards
  // For global controls, etc..
  controls?: MediaPanelCardControl;
  // This is used to connect to the shared redux store for a given media panel
  mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;

  moleculeConfig?: MoleculeConfig;

  tileMedia: TileMedia | null;
  loading?: boolean;
  config: MediaBrowserPanelConfig;
  tileMediaSpec?: TileMediaSpec;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;

  // This is used to determine if the media card is within the media browser modal
  isWithinMediaBrowserModal?: boolean;
}

type GetMediaByType<T extends Media | string> = {
  [mediaType: string]: (v: any) => T | null;
};

type GetMediaParams = {
  historyRow: RunHistoryRow | undefined;
  mediaKey: string;
};

function getMedia<T extends Media | string>(
  {historyRow, mediaKey}: GetMediaParams,
  getMediaByType: GetMediaByType<T>
): T | null {
  const mediaWBValue = historyRow?.[mediaKey];
  if (mediaWBValue == null) {
    return null;
  }

  const getMediaFn = getMediaByType[mediaWBValue._type];
  return getMediaFn?.(mediaWBValue) ?? null;
}

export function getAudioMedia(
  params: GetMediaParams,
  mediaIndex: number
): Media | null {
  return getMedia<Media>(params, {
    audio: v => v.audio?.[mediaIndex] ?? null,
    'audio-file': v => v,
  });
}

export function getImageMedia(
  params: GetMediaParams,
  mediaIndex: number
): Media | null {
  return getMedia<Media>(params, {
    images: v => v,
    'image-file': v => v,
    'images/separated': v => {
      const pathSegment = v.filenames?.[mediaIndex];
      if (pathSegment == null) {
        return v;
      }
      return {path: pathSegment, ...v};
    },
  });
}

export function getVideoMedia(
  params: GetMediaParams,
  mediaIndex: number
): Media | null {
  return getMedia<Media>(params, {
    videos: v => v.videos?.[mediaIndex] ?? null,
    'video-file': v => v,
  });
}

export function getTableMediaPath(params: GetMediaParams): string | null {
  return getMedia<string>(params, {
    'table-file': v => v.path,
  });
}

export function getPlotlyMediaPath(params: GetMediaParams): string | null {
  return getMedia<string>(params, {
    'plotly-file': v => v.path,
  });
}

export function getHTMLMedia(
  params: GetMediaParams,
  mediaIndex: number
): Media | null {
  return getMedia<Media>(params, {
    html: v => v.html?.[mediaIndex] ?? null,
    'html-file': v => v,
  });
}

export function getObject3DMediaPath(
  params: GetMediaParams,
  mediaIndex: number
): string | null {
  return getMedia<string>(params, {
    object3D: v => {
      const pathSegment = v.filenames?.[mediaIndex];
      if (pathSegment == null) {
        return null;
      }
      return `media/object3D/${pathSegment}`;
    },
    'object3D-file': v => v.path,
  });
}

export function getBokehMediaPath(
  params: GetMediaParams,
  mediaIndex: number
): string | null {
  return getMedia<string>(params, {
    bokeh: v => {
      const pathSegment = v.filenames?.[mediaIndex];
      if (pathSegment == null) {
        return null;
      }
      return `media/bokeh/${pathSegment}`;
    },
    'bokeh-file': v => v.path,
  });
}

export function getMoleculeMediaPath(
  params: GetMediaParams,
  mediaIndex: number
): string | null {
  return getMedia<string>(params, {
    molecule: v => v.filenames?.[mediaIndex] ?? null,
    'molecule-file': v => v.path,
  });
}
